import React from "react"
import {
  type EntityParty,
  type EntityTransaction,
  type IntervalOrigin,
  ObjectKind,
  ProviderType,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import { CopyableTextHoverPopover } from "@digits-shared/DesignSystem/Popovers/CopyableTextHoverPopover"
import { CellText } from "@digits-shared/DesignSystem/Tables/tableStyles"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { useOnBodyClick } from "@digits-shared/hooks/useOnBodyClick"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import { LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import { MissingPartyIcon } from "src/frontend/components/Shared/Transactions/Icons/MissingPartyIcon"
import { isTransaction } from "src/frontend/components/Shared/Transactions/types"
import { useAllowTransactionsTableEdits } from "src/frontend/components/Shared/Transactions/Updates/useAllowTransactionsTableEdits"
import { useUserSettings } from "src/frontend/hooks/useUserSettings"
import type FrontendSession from "src/frontend/session"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"
import { DEFAULT_PARTY_ICON_SIZE } from "src/shared/components/PartyHover/PartyIconHover"
import { PartyIconHoverPortal } from "src/shared/components/PartyHover/PartyIconHoverPortal"
import { type PartyTypeaheadResult } from "src/shared/components/Typeahead/sharedTypeahead"
import { Typeahead } from "src/shared/components/Typeahead/Typeahead"
import { useTypeaheadState } from "src/shared/components/Typeahead/useTypeaheadState"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const Chevron = styled(SvgChevronDown)`
  width: 24px;
  min-width: 24px;
  height: 24px;
  ${svgPathStyles(colors.secondary, 1.5)};
  margin: 0 8px;

  &.iconOnly {
    width: 16px;
    height: 16px;
    min-width: 16px;
    margin: 0 4px 0 -4px;
  }
`

const PartyTextContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  overflow-x: hidden;
`

const TransactionDescription = styled(LabelText)`
  width: 100%;
  color: ${colors.translucentSecondary60};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const HoverSplitContainer = styled.div`
  position: relative;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  overflow-x: hidden;
  transition:
    background 0.2s ease-in-out,
    border-color 0.2s ease-in-out;
  gap: 8px;
  margin-left: -4px;
  padding-left: 4px;

  border: 1px solid ${colors.secondary20};

  &:not(.disabled) {
    &:hover {
      border-color: ${colors.secondary50};
    }
  }

  &.active {
    border-color: ${colors.accentBlue};
    background: ${colors.white};
  }

  &.disabled {
    border-color: ${colors.transparent};
    ${Chevron} {
      display: none;
    }
  }
`

interface EditablePartyCellProps {
  transaction: EntityTransaction
  editable?: boolean
  onPartySelected: (party: EntityParty) => void
  updatedParty: EntityParty | undefined
  closedTimestamp: number | undefined | null
  intervalOrigin?: IntervalOrigin
  iconOnly?: boolean
}

export const EditablePartyCell: React.FC<EditablePartyCellProps> = ({
  transaction,
  editable,
  onPartySelected,
  updatedParty,
  closedTimestamp,
  intervalOrigin,
  iconOnly = false,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { currentLegalEntityId } = useSession<FrontendSession>()
  const defaultIntervalOrigin = useIntervalOrigin()
  const party = updatedParty || transaction.party

  const { allowEdits } = useAllowTransactionsTableEdits()
  const {
    settings: { editLedgerEnabled },
  } = useUserSettings()

  const typeaheadDisabled = React.useMemo(
    () =>
      !editable ||
      !editLedgerEnabled ||
      (isTransaction(transaction) &&
        transaction.sources?.some(({ name }) => name === ProviderType.QuickBooks)) ||
      !allowEdits ||
      (closedTimestamp ? closedTimestamp >= transaction.timestamp : false),
    [editable, editLedgerEnabled, transaction, allowEdits, closedTimestamp]
  )

  const { isTypeaheadShown, hideTypeahead, onClick, onKeyDown, ref } = useTypeaheadState(
    containerRef,
    typeaheadDisabled
  )
  useOnBodyClick(ref, hideTypeahead)

  const onResultSelected = React.useCallback(
    (c: PartyTypeaheadResult) => {
      hideTypeahead()
      onPartySelected(c.entity)
    },
    [onPartySelected, hideTypeahead]
  )

  const hoverOnIconAndName = typeaheadDisabled && !!party

  const iconTriggerContent = hoverOnIconAndName ? (
    <PartyIcon size={DEFAULT_PARTY_ICON_SIZE} party={party} />
  ) : undefined

  return (
    <HoverSplitContainer
      onClick={typeaheadDisabled ? undefined : onClick}
      onKeyDown={onKeyDown}
      className={stringHelper.classNames(
        typeaheadDisabled ? "disabled" : "",
        updatedParty ? "active" : ""
      )}
      ref={containerRef}
    >
      <>
        {party ? (
          <PartyIconHoverPortal
            withClickTarget
            party={party}
            intervalOrigin={intervalOrigin || defaultIntervalOrigin}
            legalEntityId={currentLegalEntityId}
            triggerContent={iconTriggerContent}
          />
        ) : (
          <MissingPartyIcon />
        )}
      </>

      {!iconOnly && (
        <PartyTextContainer>
          {party && typeaheadDisabled ? (
            <PartyIconHoverPortal
              party={party}
              intervalOrigin={intervalOrigin || defaultIntervalOrigin}
              legalEntityId={currentLegalEntityId}
              triggerContent={<CellText>{party.name}</CellText>}
            />
          ) : (
            <CellText>{party?.name || "No Party"}</CellText>
          )}

          <CopyableTextHoverPopover text={transaction.description} disabled={!typeaheadDisabled}>
            <TransactionDescription>{transaction.description}</TransactionDescription>
          </CopyableTextHoverPopover>
        </PartyTextContainer>
      )}

      <Chevron className={stringHelper.classNames(iconOnly && "iconOnly")} />

      {isTypeaheadShown && (
        <Typeahead
          ref={ref}
          kind={ObjectKind.Party}
          onResultSelected={onResultSelected}
          close={hideTypeahead}
          allowAddNew
        />
      )}
    </HoverSplitContainer>
  )
}
